import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router';

// Context providers (SAT-16)
import {
  ThemeProvider,
  UserProvider,
  ConversationProvider,
  UIProvider,
  FolderProvider,
  ProjectProvider,
  UsageLimitsProvider
} from './context';

// Shared layout wrapper (provides sidebar + footer for admin/settings pages)
import { Layout, AdminShell } from './components/layout';

// Lazy-loaded page components (SAT-95 - Code splitting)
const HomePage = lazy(() => import('./pages/HomePage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const UsagePage = lazy(() => import('./pages/UsagePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AdminIndexPage = lazy(() => import('./pages/AdminIndexPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminLocationsPage = lazy(() => import('./pages/AdminLocationsPage'));
const AdminSkillsPage = lazy(() => import('./pages/AdminSkillsPage'));
const CreateOrganization = lazy(() => import('./pages/admin/CreateOrganization'));
const AdminPlatformSkillsPage = lazy(() => import('./pages/admin/AdminPlatformSkillsPage'));
const AdminOnboardingPage = lazy(() => import('./pages/admin/AdminOnboardingPage'));
const AdminUsagePage = lazy(() => import('./pages/admin/AdminUsagePage'));
const AdminModelsPage = lazy(() => import('./pages/admin/AdminModelsPage'));
const AdminAiAllowancePage = lazy(() => import('./pages/admin/AdminAiAllowancePage'));
const AdminDisplayPage = lazy(() => import('./pages/admin/AdminDisplayPage'));
const AdminContextPage = lazy(() => import('./pages/AdminContextPage'));
const AdminSrmPage = lazy(() => import('./pages/AdminSrmPage'));
const AdminSignupsPage = lazy(() => import('./pages/AdminSignupsPage'));
const AdminDomainsPage = lazy(() => import('./pages/AdminDomainsPage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
// DOS-359 Phase D — welcome wizard for onboarding a new/existing dealer
const OnboardingWizardPage = lazy(() => import('./pages/OnboardingWizardPage'));

/** TODO #87: the token / cost dashboard only where usage is shown. */
function UsageGate({ children, fallback }) {
  const { showUsage, loaded } = useDisplaySettings();
  if (!loaded) return null;
  return showUsage ? children : fallback;
}

// UI components (loaded immediately as they're needed on mount)
import { OnboardingModal, PWAInstallBanner, ErrorBoundary, UsageWarningBanner, AdminGuard } from './components/ui';

// Auth gate (DOS-30)
import LoginPage from './pages/LoginPage';
import { useUser } from './context/UserContext';
import { useDisplaySettings } from './hooks/useDisplaySettings';

// Page loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-decisiv-blue/30 border-t-decisiv-blue rounded-full animate-spin" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

/**
 * AuthGate - Shows LoginPage when not authenticated (DOS-30). Everything but
 * the email-verification landing needs a signed-in user; there is no anonymous
 * access (skills plan step 4a).
 */
function AuthGate({ children }) {
  // initialAuthLoading (not generic `loading`) — see UserContext.jsx for why.
  // Watching `loading` would unmount LoginPage on every login attempt, throwing
  // away its local state mid-handler. See TODO #34.
  const { isAuthenticated, initialAuthLoading } = useUser();
  const { pathname } = useLocation();
  if (initialAuthLoading) return <PageLoader />;
  // Public route (no auth required): /verify-email (token landing — exchanges
  // token for a session).
  const publicPaths = pathname === '/verify-email';
  if (!isAuthenticated && !publicPaths) return <LoginPage />;
  return <>{children}</>;
}

/**
 * OnboardingHost — renders the welcome tour only when the visitor is
 * authenticated AND on the home route. AuthGate's public-path bypass means
 * /verify-email renders children even without auth; without this gate
 * the modal stacked over the verify-email view (TODO #42 Defect A, 2026-06-02).
 */
function OnboardingHost() {
  const { isAuthenticated } = useUser();
  const { pathname } = useLocation();
  if (!isAuthenticated || pathname !== '/') return null;
  return <OnboardingModal />;
}

/**
 * App - Main application component with routing and context providers
 *
 * Context Providers (outer to inner):
 * - ThemeProvider: Light/dark mode theming
 * - UserProvider: Authentication state + RBAC (DOS-25)
 * - ConversationProvider: Active conversation and messages
 * - FolderProvider: Conversation folder organization (SAT-46)
 * - UIProvider: Panel visibility and UI state
 *
 * Routes:
 * - / : Home page with new chat
 * - /chat/:id : Individual conversation
 * - /settings : Application settings
 * - /share/:token : Shared conversation view
 * - /usage : Token usage dashboard
 * - /admin/users : Org user management (org_admin only, DOS-29)
 * - * : 404 Not Found fallback
 */
function App() {
  return (
    <ThemeProvider>
      <UserProvider>
        <ConversationProvider>
          <FolderProvider>
            <ProjectProvider>
              <UsageLimitsProvider>
              <UIProvider>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <div className="h-full bg-white dark:bg-gray-900 transition-colors">
                  <AuthGate>
                    {/* Onboarding welcome screen for new users (SAT-84).
                        Gated to authenticated + home route via OnboardingHost
                        so it can't leak onto /verify-email. */}
                    <OnboardingHost />
                    {/* PWA install banner (SAT-93) — admin only (DOS-28) */}
                    <AdminGuard requiredRole="org_admin">
                      <PWAInstallBanner />
                    </AdminGuard>
                    {/* Usage limit warnings (SAT-110) — admin only (DOS-28) */}
                    <AdminGuard requiredRole="org_admin">
                      <UsageWarningBanner />
                    </AdminGuard>
                    {/* Suspense boundary for lazy-loaded routes (SAT-95) */}
                    <Suspense fallback={<PageLoader />}>
                    {/* Error boundaries around routes (SAT-96) */}
                    <Routes>
                      {/* Home - New conversation / Welcome */}
                      <Route path="/" element={
                        <ErrorBoundary level="page">
                          <HomePage />
                        </ErrorBoundary>
                      } />

                      {/* Chat - Individual conversation by ID */}
                      <Route path="/chat/:id" element={
                        <ErrorBoundary level="page">
                          <ChatPage />
                        </ErrorBoundary>
                      } />

                      {/* Settings - Application settings */}
                      <Route path="/settings" element={
                        <ErrorBoundary level="page">
                          <SettingsPage />
                        </ErrorBoundary>
                      } />

                      {/* Share - Read-only shared conversation */}
                      <Route path="/share/:token" element={
                        <ErrorBoundary level="page">
                          <SharePage />
                        </ErrorBoundary>
                      } />

                      {/* Usage - Token usage dashboard (SAT-80) — admin only (DOS-28) */}
                      <Route path="/usage" element={
                        <ErrorBoundary level="page">
                          <AdminGuard requiredRole="manager" fallback={<NotFoundPage />}>
                            <UsageGate fallback={<NotFoundPage />}>
                              <Layout showArtifactPanel={false}><UsagePage /></Layout>
                            </UsageGate>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/*
                        DOS-388 admin routes.
                        Each /admin/* page: <ErrorBoundary> ▸ <AdminGuard fallback=<Navigate/>> ▸ <Layout> ▸ <AdminShell> ▸ page.
                        Per-page role/capability match the ADMIN_NAV config in src/config/adminNav.js.
                        The AdminGuard fallback routes to /admin (landing); landing self-redirects to / if the user has zero admin capabilities.
                      */}

                      {/* Admin landing */}
                      <Route path="/admin" element={
                        <ErrorBoundary level="page">
                          <Layout showArtifactPanel={false}>
                            <AdminShell><AdminIndexPage /></AdminShell>
                          </Layout>
                        </ErrorBoundary>
                      } />

                      {/* Admin - Org user management (DOS-29) */}
                      <Route path="/admin/users" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredAnyCapability={['manage_users', 'manage_users_own_location']}
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminUsersPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* Admin - Location management (DOS-64) */}
                      <Route path="/admin/locations" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredAnyCapability={['edit_locations', 'edit_location_own']}
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminLocationsPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* Admin - Skills management (DOS-64) */}
                      <Route path="/admin/skills" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredCapability="manage_skills"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminSkillsPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* DOS-388 Phase C — Platform skills (superadmin) */}
                      <Route path="/admin/skills/platform" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredRole="superadmin"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminPlatformSkillsPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* Admin - Context file editor (ADR-005 P4) */}
                      <Route path="/admin/context" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredAnyCapability={['edit_context', 'edit_context_own_location']}
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminContextPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* Admin - SRM Credential Management (DOS-195) */}
                      <Route path="/admin/srm" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredCapability="manage_srm_credentials"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminSrmPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* Admin - New organization onboarding wizard (DOS-65) */}
                      <Route path="/admin/new-org" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredCapability="create_org"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><CreateOrganization /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* DOS-392: Admin - cross-tenant usage / cost rollup (superadmin) */}
                      {/* DOS-415 Phase 4c: Admin - model catalog (superadmin) */}
                      <Route path="/admin/models" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredRole="superadmin"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminModelsPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* Monthly AI allowance in USD (superadmin) */}
                      <Route path="/admin/ai-allowance" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredRole="superadmin"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminAiAllowancePage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* Chat display — thinking indicator vs full (superadmin) */}
                      <Route path="/admin/display" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredRole="superadmin"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminDisplayPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      <Route path="/admin/usage" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredRole="superadmin"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminUsagePage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* DOS-360 Phase C: Admin - onboarding health dashboard (superadmin) */}
                      <Route path="/admin/onboarding" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredRole="superadmin"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminOnboardingPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* Phase 4: Admin - signup approval queue + suspend/reinstate (superadmin) */}
                      <Route path="/admin/signups" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredRole="superadmin"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminSignupsPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* Phase 4: Admin - trusted + blocked domain CRUD (superadmin) */}
                      <Route path="/admin/domains" element={
                        <ErrorBoundary level="page">
                          <AdminGuard
                            requiredRole="superadmin"
                            fallback={<Navigate to="/admin" replace />}
                          >
                            <Layout showArtifactPanel={false}>
                              <AdminShell><AdminDomainsPage /></AdminShell>
                            </Layout>
                          </AdminGuard>
                        </ErrorBoundary>
                      } />

                      {/* Email verification landing — public, before auth */}
                      <Route path="/verify-email" element={<LoginPage />} />

                      {/* Skill catalog — the org's skills (skills plan step 4a) */}
                      <Route path="/catalog" element={
                        <ErrorBoundary level="page">
                          <CatalogPage />
                        </ErrorBoundary>
                      } />

                      {/* The demo catalog moved: /demo-browse → /catalog */}
                      <Route path="/demo-browse" element={<Navigate to="/catalog" replace />} />

                      {/* DOS-359 Phase D — welcome wizard (new + existing dealers) */}
                      <Route path="/onboarding/new" element={
                        <ErrorBoundary level="page">
                          <OnboardingWizardPage />
                        </ErrorBoundary>
                      } />
                      <Route path="/onboarding/:dealerId" element={
                        <ErrorBoundary level="page">
                          <OnboardingWizardPage />
                        </ErrorBoundary>
                      } />

                      {/* 404 - Catch all unmatched routes */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    </Suspense>
                  </AuthGate>
                </div>
              </BrowserRouter>
              </UIProvider>
              </UsageLimitsProvider>
            </ProjectProvider>
          </FolderProvider>
        </ConversationProvider>
      </UserProvider>
    </ThemeProvider>
  );
}

export default App;
